import { UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import _ from "lodash";
import React, { createContext, useEffect, useState } from "react";
import {
  RoleName,
  SubscriptionType,
  UserRoleName,
  UserRoles,
} from "../../interfaces/rbac";

type ReemiaUserClaim = UserClaims & {
  first_name: string;
  last_name: string;
  company_id: string;
  company_alphabetic_name: string;
  permissions: string;
};

export interface Session {
  user: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  org: {
    id: string;
    name: string;
  };
  roles?: UserRoles;
  OrionUserGroups: string[];
  OrionContributorGroups: string[];
  OrionManagerGroups: string[];
  OrionChatGroups: string[];
  SiriusUserGroups: string[];
  SiriusContributorGroups: string[];
  SiriusManagerGroups: string[];
  ManagerGroups: string[];
  isAdmin: boolean;
}

const SessionContext = createContext<Session | undefined>(undefined);

const useSession = () => React.useContext(SessionContext);

export const SessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { authState } = useOktaAuth();

  useEffect(() => {
    if (
      !authState?.isAuthenticated ||
      !authState?.accessToken?.accessToken ||
      !authState?.accessToken?.claims
    ) {
      return;
    }

    const claims = authState.accessToken.claims as ReemiaUserClaim;
    const roles = authState.accessToken.claims.permissions
      ? JSON.parse(authState.accessToken.claims.permissions as string)
      : undefined;

    setSession({
      user: {
        id: claims.sub,
        email: claims.sub,
        first_name: claims.first_name,
        last_name: claims.last_name,
      },
      org: {
        id: claims.company_id,
        name: claims.company_alphabetic_name,
      },
      roles: roles,
      OrionUserGroups: OrionUserGroups(roles),
      OrionContributorGroups: OrionContributorGroups(roles),
      OrionManagerGroups: OrionManagerGroups(roles),
      OrionChatGroups: OrionChatGroups(roles),
      SiriusUserGroups: SiriusUserGroups(roles),
      SiriusContributorGroups: SiriusContributorGroups(roles),
      SiriusManagerGroups: SiriusManagerGroups(roles),
      ManagerGroups: ManagerGroups(roles),
      isAdmin: isAdmin(roles),
    });
  }, [authState?.accessToken, authState?.isAuthenticated]);

  const [session, setSession] = useState<Session | undefined>(undefined);
  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
};

export function OrionUserGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) =>
          [SubscriptionType.ORION, SubscriptionType.PLATFORM].includes(
            role.subscription
          )
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionContributorGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.ORION, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && [RoleName.CONTRIBUTOR, RoleName.MANAGER].includes(role.role)
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.ORION, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && RoleName.MANAGER === role.role
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionChatGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) => SubscriptionType.PLATFORM === role.subscription)
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusUserGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) =>
          [SubscriptionType.SIRIUS, SubscriptionType.PLATFORM].includes(
            role.subscription
          )
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusContributorGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.SIRIUS, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && [RoleName.CONTRIBUTOR, RoleName.MANAGER].includes(role.role)
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [SubscriptionType.SIRIUS, SubscriptionType.PLATFORM].includes(
              role.subscription
            ) && RoleName.MANAGER === role.role
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function ManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) => role.role === RoleName.MANAGER)
        .map(([workspace, _]) => workspace)
    : [];
}

export function isAdmin(roles: UserRoles | undefined) {
  return !!roles && roles.role === UserRoleName.ADMIN;
}

export default useSession;
